import React, { FC, useEffect, useRef, useState } from 'react';
import { useVideoThumbnails } from './useVideoThumbnails';

export interface ITimelineRange {
    id: string | number;
    start: number;
    end: number;
}

interface Props {
    /** The playing video element — used for the live playhead and for seeking. */
    video: HTMLVideoElement | null;
    /** Source URL for thumbnail extraction (usually video.currentSrc). */
    videoUrl?: string;
    duration: number;
    rangeStart: number;
    rangeEnd: number;
    onRangeChange: (start: number, end: number) => void;
    /** Minimum selectable range in seconds. */
    minRange?: number;
    /** Additional read-only ranges to decorate the strip with (e.g. existing comments). */
    ranges?: ITimelineRange[];
    className?: string;
}

const SEEK_THROTTLE_MS = 80;
const ACCENT = 'var(--accent)';

const clampNum = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

export const VideoTimeline: FC<Props> = ({
    video,
    videoUrl,
    duration,
    rangeStart,
    rangeEnd,
    onRangeChange,
    minRange = 0.5,
    ranges,
    className
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const playheadRef = useRef<HTMLDivElement>(null);
    const dragTargetRef = useRef<'start' | 'end' | null>(null);
    const lastSeekAtRef = useRef(0);
    const [timelineWidth, setTimelineWidth] = useState(0);

    const { thumbnails, totalCount } = useVideoThumbnails(videoUrl, duration, timelineWidth);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const measure = () => setTimelineWidth(el.clientWidth);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Playhead is positioned imperatively at rAF cadence — the context's timeupdate
    // ticks (~4/sec) are too coarse for a smooth sweep, and this avoids re-rendering.
    useEffect(() => {
        if (!video || !(duration > 0)) return undefined;
        let frame: number;
        const tick = () => {
            if (playheadRef.current) {
                const pct = clampNum(video.currentTime / duration, 0, 1) * 100;
                playheadRef.current.style.left = `${pct}%`;
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [video, duration]);

    const xToTime = (clientX: number) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return 0;
        return clampNum((clientX - rect.left) / rect.width, 0, 1) * duration;
    };

    const seekVideo = (time: number, force = false) => {
        if (!video) return;
        const now = performance.now();
        if (!force && now - lastSeekAtRef.current < SEEK_THROTTLE_MS) return;
        lastSeekAtRef.current = now;
        video.currentTime = time;
    };

    const moveHandle = (which: 'start' | 'end', time: number) => {
        if (which === 'start') {
            const next = clampNum(time, 0, Math.max(rangeEnd - minRange, 0));
            onRangeChange(next, rangeEnd);
            seekVideo(next);
        } else {
            const next = clampNum(time, Math.min(rangeStart + minRange, duration), duration);
            onRangeChange(rangeStart, next);
            seekVideo(next);
        }
    };

    const onHandlePointerDown = (which: 'start' | 'end') => (e: React.PointerEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragTargetRef.current = which;
        video?.pause();
    };

    const onHandlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const which = dragTargetRef.current;
        if (!which) return;
        moveHandle(which, xToTime(e.clientX));
    };

    const onHandlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        const which = dragTargetRef.current;
        dragTargetRef.current = null;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        // Land exactly on the final handle position (moves may have been throttled).
        if (which) seekVideo(which === 'start' ? rangeStart : rangeEnd, true);
    };

    const onTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        seekVideo(xToTime(e.clientX), true);
    };

    const onHandleKeyDown = (which: 'start' | 'end') => (e: React.KeyboardEvent) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const delta = e.key === 'ArrowLeft' ? -1 : 1;
        moveHandle(which, (which === 'start' ? rangeStart : rangeEnd) + delta);
    };

    const startPct = duration > 0 ? (clampNum(rangeStart, 0, duration) / duration) * 100 : 0;
    const endPct = duration > 0 ? (clampNum(rangeEnd, 0, duration) / duration) * 100 : 0;

    const renderHandle = (which: 'start' | 'end') => (
        <div
            role="slider"
            tabIndex={0}
            aria-label={which === 'start' ? 'Range start' : 'Range end'}
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(which === 'start' ? rangeStart : rangeEnd)}
            className="absolute inset-y-0 w-7 -translate-x-1/2 flex items-center justify-center cursor-ew-resize z-20 outline-none focus-visible:ring-2 focus-visible:ring-white"
            style={{ left: `${which === 'start' ? startPct : endPct}%` }}
            onPointerDown={onHandlePointerDown(which)}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onPointerCancel={onHandlePointerUp}
            onKeyDown={onHandleKeyDown(which)}
        >
            <div
                className="h-full w-2.5 rounded-md border border-white/70 flex items-center justify-center shadow-md"
                style={{ backgroundColor: ACCENT }}
            >
                <span className="w-0.5 h-4 bg-white/90 rounded" />
            </div>
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={`relative h-16 w-full select-none touch-none ${className || ''}`}
            onPointerDown={onTrackPointerDown}
        >
            <div className="absolute inset-0 rounded-xl overflow-hidden bg-neutral-900">
                {/* Layer 1: thumbnail strip */}
                <div className="absolute inset-0 flex pointer-events-none">
                    {Array.from({ length: totalCount }, (_, i) =>
                        thumbnails[i] ? (
                            <img
                                key={i}
                                src={thumbnails[i]}
                                alt=""
                                draggable={false}
                                className="h-full flex-1 min-w-0 object-cover"
                            />
                        ) : (
                            <div key={i} className="h-full flex-1 min-w-0 bg-neutral-800 animate-pulse" />
                        )
                    )}
                </div>

                {/* Layer 2: decorations — read-only ranges (existing comments, markers) */}
                {ranges?.map((range) => {
                    const left = duration > 0 ? (clampNum(range.start, 0, duration) / duration) * 100 : 0;
                    const right = duration > 0 ? (clampNum(range.end, 0, duration) / duration) * 100 : 0;
                    return (
                        <div
                            key={range.id}
                            className="absolute inset-y-0 bg-white/25 pointer-events-none"
                            style={{ left: `${left}%`, width: `${Math.max(right - left, 0)}%` }}
                        />
                    );
                })}

                {/* Layer 3: active selection — dim outside, outline inside */}
                <div
                    className="absolute inset-y-0 left-0 bg-black/60 pointer-events-none"
                    style={{ width: `${startPct}%` }}
                />
                <div
                    className="absolute inset-y-0 right-0 bg-black/60 pointer-events-none"
                    style={{ width: `${Math.max(100 - endPct, 0)}%` }}
                />
                <div
                    className="absolute inset-y-0 border-y-2 pointer-events-none"
                    style={{
                        left: `${startPct}%`,
                        width: `${Math.max(endPct - startPct, 0)}%`,
                        borderColor: ACCENT
                    }}
                />

                {/* Layer 4: playhead (positioned via rAF) */}
                <div
                    ref={playheadRef}
                    className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow pointer-events-none"
                    style={{ left: 0 }}
                />
            </div>

            {renderHandle('start')}
            {renderHandle('end')}
        </div>
    );
};
